import React, { useRef, useState, useEffect } from 'react';
import { SearchOutlined } from '@ant-design/icons';
import type { InputRef, TableColumnsType, TableColumnType } from 'antd';
import { Button, Input, Space, Table, notification } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import "./browser_picking_list_return.css";
import { PickingListHeader } from '../../components/hoc_picking_list_return';
import axios from 'axios';
import { useAuth } from "../../context/AuthContext";
import useSWR from 'swr';
const fetcher = (url: string) => axios.get(url).then(res => res.data);

interface DataType extends PickingListHeader {
    key: string;
}


type DataIndex = keyof DataType;
interface BrowserProps {
    onSelect: (record: PickingListHeader) => void
    onCancel: () => void;
}


const BrowserPickingListReturn: React.FC<BrowserProps> = ({ onSelect, onCancel }) => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    const [isProgress, setIsProgress] = useState(false);
    const { userData } = useAuth();
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const [eligibleSOs, setEligibleSOs] = useState<Set<string>>(new Set());
    const searchInput = useRef<InputRef>(null);
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const { data: rawData } = useSWR(`${backendUrl}/api/picking-list`, fetcher, { refreshInterval: 5000 });

    const data = React.useMemo(() => {
        if (!rawData) return [];
        return rawData
            .filter((item: any) => item.status === '7' && item.warehouse_code === userData?.warehouse_code)
            .map((item: any) => ({
                ...item,
                key: item.id.toString(),
            }));
    }, [rawData, userData]);

    useEffect(() => {
        const fetchEligibleSOs = async () => {
            try {
                const res = await axios.get(`${backendUrl}/api/picking-list-return/eligible`);
                const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
                setEligibleSOs(new Set(list.map((item: any) => item.so_no)));
            } catch (error) {
                console.error('Gagal mengambil daftar SO eligible untuk return:', error);
            }
        };
        fetchEligibleSOs();
    }, [backendUrl]);


    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const handleRowClick = (record: PickingListHeader) => {
        if (isProgress) return;
        const soNo = record.so_no || '';
        if (!eligibleSOs.has(soNo)) {
            notification.warning({
                message: 'Tidak Bisa Dipilih',
                description: `SO ${soNo} tidak memiliki item yang dapat dikembalikan (semua item sudah packed, invoiced, atau fully returned).`,
            });
            return;
        }
        setIsProgress(true);
        try {
            onSelect(record);
        } catch (error) {
            console.error('Error selecting picking list:', error);
            notification.error({
                message: 'Error',
                description: 'Gagal memilih data picking list.',
            });
        } finally {
            setIsProgress(false);
        }
    };


    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<DataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}

                    placeholder={`Search ${dataIndex.replace(/_/g, ' ')}`}

                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) => {
            const val = record[dataIndex];
            return val !== undefined && val !== null
                ? val.toString().toLowerCase().includes((value as string).toLowerCase())
                : false;
        },
        filterDropdownProps: {
            onOpenChange(open) {
                if (open) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });


    const columns: TableColumnsType<DataType> = [
        {
            title: 'Sales Order No.',
            dataIndex: 'so_no',
            key: 'so_no',
            ...getColumnSearchProps('so_no'),
            sorter: (a, b) => (a.so_no || '').localeCompare(b.so_no || ''),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Customer',
            dataIndex: 'cs_name',
            key: 'cs_name',
            ...getColumnSearchProps('cs_name'),
            sorter: (a, b) => (a.cs_name || '').localeCompare(b.cs_name || ''),
            sortDirections: ['descend', 'ascend'],
        },
    ];


    return (

        <div id="ilzh" style={transformStyle}>
            <div className="gjs-grid-row" id="i1zl">
                <div className="gjs-grid-column" id="icvj">
                    <Table<DataType>
                        columns={columns}
                        dataSource={data}
                        loading={isProgress}
                        pagination={{ pageSize: 10 }}
                        onRow={(record) => {
                            return {
                                onClick: () => {
                                    handleRowClick(record);
                                },
                            };
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default BrowserPickingListReturn;